import { Patient } from "@/services/patient-service";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

interface DeletePatientDialogProps {
  patient: Patient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (id: string) => void;
}

export function DeletePatientDialog({ patient, open, onOpenChange, onDelete }: DeletePatientDialogProps) {
  if (!patient) return null;

  const handleDelete = () => { onDelete(patient.id); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Deletar Paciente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
            <p className="text-xs text-destructive font-medium">
              Atenção: Esta ação não pode ser desfeita. Todos os dados do paciente serão permanentemente removidos.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="filter-label">Nome</Label>
            <Input className="filter-input" value={patient.name} disabled />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label">CPF</Label>
              <Input className="filter-input" value={patient.cpf} disabled />
            </div>
            <div className="space-y-1">
              <Label className="filter-label">Data de Nascimento</Label>
              <Input className="filter-input" value={patient.birth_date ? new Date(patient.birth_date).toLocaleDateString('pt-BR') : ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label">Email</Label>
              <Input className="filter-input" value={patient.email} disabled />
            </div>
            <div className="space-y-1">
              <Label className="filter-label">Telefone</Label>
              <Input className="filter-input" value={patient.cellphone} disabled />
            </div>
          </div>

          {(patient.dental_insurance_id || patient.card_number) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="filter-label">Convênio (ID)</Label>
                <Input className="filter-input" value={patient.dental_insurance_id || ""} disabled placeholder="Sem convênio" />
              </div>
              {patient.card_number && (
                <div className="space-y-1">
                  <Label className="filter-label">Carteirinha</Label>
                  <Input className="filter-input" value={patient.card_number} disabled />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="destructive" size="sm" className="btn-header" onClick={handleDelete}>Deletar Paciente</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
